'use client';

import { c as _c } from "react/compiler-runtime";
import { delay } from 'es-toolkit';
import { useEffect, useRef } from 'react';
import { PageNavigation } from '@/components/client';
import { useStatusBarColor } from '@/hooks/useStatusBarColor';
import { STATUS_COLOR } from '@/stores/DeviceStore';
import { useGlobalStore } from '@/stores/GlobalStore';
import { useChatbotReducer } from '../_hooks/useChatbotReducer';
import { useTypingAnimation } from '../_hooks/useTypingAnimation';
import { INFO_TEXT, QUESTIONS, WELCOME_TEXT } from '../_lib/constants';
import { ChatMessage } from './ChatMessage';
import { ResultStep } from './ResultStep';
export const ChatbotFlow = () => {
  const $ = _c(46);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      color: STATUS_COLOR.CHATBOT
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useStatusBarColor(t0);
  const {
    state,
    actions,
    derived
  } = useChatbotReducer();
  const setHideBottomNav = useGlobalStore(_temp);
  const {
    messages,
    answers
  } = state;
  const {
    showResultStep,
    currentQuestion
  } = derived;
  const chatEndRef = useRef(null);
  let t1;
  let t2;
  if ($[1] !== setHideBottomNav) {
    t1 = () => {
      setHideBottomNav(false);
      return () => {
        setHideBottomNav(false);
      };
    };
    t2 = [setHideBottomNav];
    $[1] = setHideBottomNav;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      text: INFO_TEXT,
      speed: 25,
      delay: 300
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    displayedText: typingText,
    isComplete: isTypingComplete
  } = useTypingAnimation(t3);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      chatEndRef.current?.scrollIntoView();
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const scrollToBottom = t4;
  let t5;
  let t6;
  if ($[6] !== actions || $[7] !== isTypingComplete || $[8] !== messages.length) {
    t5 = () => {
      if (isTypingComplete && messages.length === 0) {
        actions.addMessage("bot", WELCOME_TEXT);
      }
    };
    t6 = [isTypingComplete, messages.length, actions];
    $[6] = actions;
    $[7] = isTypingComplete;
    $[8] = messages.length;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  useEffect(t5, t6);
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      chatEndRef.current?.scrollIntoView();
    };
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== messages.length) {
    t8 = [messages.length];
    $[12] = messages.length;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  useEffect(t7, t8);
  let t9;
  if ($[14] !== actions || $[15] !== setHideBottomNav) {
    t9 = async () => {
      actions.addMessage("bot", "\uB2F5\uBCC0 \uC644\uB8CC!\n\uB9DE\uCDA4 \uC815\uCC45\uC744 \uCC3E\uACE0 \uC788\uC5B4\uC694...");
      await delay(1500);
      setHideBottomNav(true);
      actions.showResult();
    };
    $[14] = actions;
    $[15] = setHideBottomNav;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const showFinalResult = t9;
  let t10;
  if ($[17] !== actions || $[18] !== setHideBottomNav) {
    t10 = () => {
      setHideBottomNav(false);
      actions.restart();
    };
    $[17] = actions;
    $[18] = setHideBottomNav;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const handleRestart = t10;
  let t11;
  if ($[20] !== actions) {
    t11 = () => {
      actions.addMessage("bot", QUESTIONS[0].text, QUESTIONS[0].options);
      actions.nextStep();
    };
    $[20] = actions;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const start = t11;
  let t12;
  if ($[22] !== actions || $[23] !== answers || $[24] !== currentQuestion || $[25] !== showFinalResult || $[26] !== state.step) {
    t12 = async value => {
      if (!currentQuestion) {
        return;
      }
      const selected = currentQuestion.options.find(o => o.value === value);
      if (!selected) {
        return;
      }
      actions.addMessage("user", selected.label);
      actions.setAnswer(currentQuestion.key, value);
      const newAnswers = {
        ...answers,
        [currentQuestion.key]: value
      };
      await delay(300);
      const reaction = currentQuestion.reactions[value];
      if (reaction) {
        actions.addMessage("bot", reaction);
      }
      await delay(600);
      if (state.step < QUESTIONS.length) {
        let nextIndex = state.step;
        if (QUESTIONS[nextIndex]?.key === "hasChild" && newAnswers.age === "S") {
          nextIndex = nextIndex + 1;
          actions.nextStep();
        }
        if (nextIndex < QUESTIONS.length) {
          const next = QUESTIONS[nextIndex];
          actions.addMessage("bot", next.text, next.options);
          actions.nextStep();
        } else {
          await showFinalResult();
        }
      } else {
        await showFinalResult();
      }
    };
    $[22] = actions;
    $[23] = answers;
    $[24] = currentQuestion;
    $[25] = showFinalResult;
    $[26] = state.step;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const handleAnswer = t12;
  if (showResultStep) {
    let t13;
    if ($[28] !== answers || $[29] !== handleRestart) {
      t13 = <ResultStep answers={answers} onRestart={handleRestart} />;
      $[28] = answers;
      $[29] = handleRestart;
      $[30] = t13;
    } else {
      t13 = $[30];
    }
    return t13;
  }
  let t13;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <PageNavigation backButton={false} style={{
      backgroundColor: "#F6F6F6"
    }} title={"\uC6F0\uB85C \uC815\uCC45 \uB9E4\uCE6D"} />;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== isTypingComplete) {
    t14 = !isTypingComplete && <span className="ml-1 inline-block h-14 w-2 animate-pulse bg-B-500" />;
    $[32] = isTypingComplete;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t14 || $[35] !== typingText) {
    t15 = <div className="mb-16 flex h-58 items-center justify-center rounded-xsmall bg-Gray-200 px-20"><p className="whitespace-pre-line text-center text-B-500 typo-caption1">{typingText}{t14}</p></div>;
    $[34] = t14;
    $[35] = typingText;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== handleAnswer || $[38] !== messages || $[39] !== start || $[40] !== state.step) {
    t16 = messages.map((message, index) => <ChatMessage key={message.id} isLatest={index === messages.length - 1} message={message} showAvatar={message.type === "bot" && messages[index - 1]?.type !== "bot"} showStartButton={state.step === 0 && index === 0} onScrollToBottom={scrollToBottom} onSelectOption={handleAnswer} onStart={start} />);
    $[37] = handleAnswer;
    $[38] = messages;
    $[39] = start;
    $[40] = state.step;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div ref={chatEndRef} />;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== t15 || $[44] !== t16) {
    t18 = <div className="mx-auto flex min-h-[calc(100vh-118px)] w-full max-w-screen-xl flex-col bg-Gray-50">{t13}<div className="flex-1 overflow-y-auto px-20 pb-32 pt-16">{t15}{t16}{t17}</div></div>;
    $[43] = t15;
    $[44] = t16;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  return t18;
};
function _temp(state_0) {
  return state_0.setHideBottomNav;
}